import { createEvent, createStore, sample } from "effector"
import { sendEventToAnalyticsFx } from "@callcenter/api"

import { selfServiceApi } from "../../api"
import { isTerminalStatus } from "../kyc.helpers"
import { $isKycReentry, continueKyc, dismissKycResult, kycStatusReceived, retryKyc } from "../kyc.model"
import {
  $isUpgradeNowModalShown,
  emailModalOpened,
  qrModalOpened,
  requestUpgrade,
  startVerificationClicked,
  underReviewOpened,
  verificationModalOpened,
  verifyOnMobileClicked
} from "../self-service.model"
import type { KycMethod, UpgradeSource } from "./self-service-analytics.constants"
import { SS_UPGRADE_EVENT } from "./self-service-analytics.constants"
import { kycResultOf, withSession } from "./self-service-analytics.helpers"

export const upgradeNowTracked = createEvent<UpgradeSource>()

const $verificationSessionId = createStore("")

sample({
  clock: [selfServiceApi.requestUpgradeFx.doneData, selfServiceApi.startKycFx.doneData],
  fn: (res) => res.reference,
  target: $verificationSessionId
})

sample({
  clock: upgradeNowTracked,
  source: { sessionId: $verificationSessionId, isRetry: $isKycReentry },
  fn: ({ sessionId, isRetry }, source: UpgradeSource) => ({
    event: SS_UPGRADE_EVENT.upgradeNowClicked,
    data: { source_page: source, is_retry: isRetry, ...withSession(sessionId) }
  }),
  target: sendEventToAnalyticsFx
})

sample({
  clock: $isUpgradeNowModalShown,
  filter: (isShown) => isShown,
  fn: () => ({ event: SS_UPGRADE_EVENT.upgradeIntroViewed, data: {} }),
  target: sendEventToAnalyticsFx
})

sample({
  clock: requestUpgrade,
  fn: () => ({ event: SS_UPGRADE_EVENT.upgradeIntroConfirmed, data: {} }),
  target: sendEventToAnalyticsFx
})

sample({
  clock: selfServiceApi.requestUpgradeFx.done,
  fn: () => ({
    event: SS_UPGRADE_EVENT.complianceGateEvaluated,
    data: { gate_decision: "approved", risk_level: "unknown" }
  }),
  target: sendEventToAnalyticsFx
})

sample({
  clock: selfServiceApi.requestUpgradeFx.fail,
  fn: () => ({
    event: SS_UPGRADE_EVENT.complianceGateEvaluated,
    data: { gate_decision: "blocked", risk_level: "unknown", block_reason: "manual_review_pending" }
  }),
  target: sendEventToAnalyticsFx
})

sample({
  clock: underReviewOpened,
  fn: () => ({ event: SS_UPGRADE_EVENT.complianceGateBlockedShown, data: { risk_level: "unknown" } }),
  target: sendEventToAnalyticsFx
})

sample({
  clock: verificationModalOpened,
  source: $verificationSessionId,
  fn: (sessionId) => ({ event: SS_UPGRADE_EVENT.kycPageViewed, data: withSession(sessionId) }),
  target: sendEventToAnalyticsFx
})

const kycMethodSelected = createEvent<KycMethod>()

sample({ clock: startVerificationClicked, fn: (): KycMethod => "desktop", target: kycMethodSelected })
sample({ clock: verifyOnMobileClicked, fn: (): KycMethod => "mobile_qr", target: kycMethodSelected })
sample({ clock: emailModalOpened, fn: (): KycMethod => "email_link", target: kycMethodSelected })

sample({
  clock: kycMethodSelected,
  source: $verificationSessionId,
  fn: (sessionId, method) => ({
    event: SS_UPGRADE_EVENT.kycMethodSelected,
    data: { kyc_method: method, ...withSession(sessionId) }
  }),
  target: sendEventToAnalyticsFx
})

sample({
  clock: qrModalOpened,
  source: $verificationSessionId,
  fn: (sessionId) => ({ event: SS_UPGRADE_EVENT.kycQrCodeShown, data: withSession(sessionId) }),
  target: sendEventToAnalyticsFx
})

sample({
  clock: selfServiceApi.sendVerificationEmailFx.done,
  source: $verificationSessionId,
  fn: (sessionId) => ({ event: SS_UPGRADE_EVENT.kycEmailLinkSent, data: withSession(sessionId) }),
  target: sendEventToAnalyticsFx
})

sample({
  clock: selfServiceApi.redirectToKycFx,
  source: $verificationSessionId,
  fn: (sessionId) => ({ event: SS_UPGRADE_EVENT.kycVerificationStarted, data: withSession(sessionId) }),
  target: sendEventToAnalyticsFx
})

sample({
  clock: kycStatusReceived,
  source: $verificationSessionId,
  filter: (_, status) => isTerminalStatus(status),
  fn: (sessionId, status) => ({
    event: SS_UPGRADE_EVENT.kycVerificationCompleted,
    data: { kyc_result: kycResultOf(status), ...withSession(sessionId) }
  }),
  target: sendEventToAnalyticsFx
})

sample({
  clock: kycStatusReceived,
  source: $verificationSessionId,
  filter: (_, status) => status === "accepted",
  fn: (sessionId) => ({ event: SS_UPGRADE_EVENT.verificationCompleteViewed, data: withSession(sessionId) }),
  target: sendEventToAnalyticsFx
})

sample({
  clock: selfServiceApi.startKycFx.fail,
  source: $verificationSessionId,
  fn: (sessionId) => ({
    event: SS_UPGRADE_EVENT.errorShown,
    data: { error_type: "shufti_error", step: "kyc_init", ...withSession(sessionId) }
  }),
  target: sendEventToAnalyticsFx
})

sample({
  clock: [retryKyc, continueKyc, dismissKycResult],
  source: $verificationSessionId,
  fn: (sessionId) => ({
    event: SS_UPGRADE_EVENT.kycVerificationStarted,
    data: { is_retry: true, ...withSession(sessionId) }
  }),
  target: sendEventToAnalyticsFx
})
